/** Setup is online-only: a cached bootstrap form may speak a retired auth protocol. */
export function isOnboardingPath(path: string): boolean {
  return /^\/(?:setup|enroll)\/?$/.test(path) || path.startsWith('/i/')
}

export const ONBOARDING_SHELL_PROBE = 'viko.invite-shell.v1'

interface OnboardingClient {
  url: string
  navigate(url: string): Promise<unknown>
}

/** Only refresh old shells. Current setup screens may have a passkey dialog open. */
export async function refreshLegacyOnboarding<T extends OnboardingClient>(
  clients: readonly T[],
  understandsInvites: (client: T) => Promise<boolean>,
): Promise<void> {
  await Promise.all(
    clients.map(async (client) => {
      if (!isOnboardingPath(new URL(client.url).pathname)) return
      if (await understandsInvites(client)) return
      // Keep the fragment: the invitation and pairing secret must not be lost or sent to a server.
      try {
        // Do not await navigation during activation: its fetch waits for activation to finish.
        void client.navigate(client.url).catch(() => {})
      } catch {
        /* Tab may have closed. */
      }
    }),
  )
}

export async function onboardingResponse(
  request: Request,
  fetcher: typeof fetch = fetch,
): Promise<Response> {
  try {
    return await fetcher(request, { cache: 'no-store' })
  } catch {
    return new Response(
      '<!doctype html><html lang="en"><meta name="viewport" content="width=device-width"><title>Viko setup</title><h1>Reconnect to finish setup</h1><p>Account creation and connecting your computer need an internet connection. Reconnect, then refresh this page.</p></html>',
      {
        status: 503,
        headers: { 'Content-Type': 'text/html; charset=utf-8', 'Cache-Control': 'no-store' },
      },
    )
  }
}
